import { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MembershipForm } from '../components/forms/MembershipForm';
import { CalendarView } from '../components/events/CalendarView';
import { MinistriesService, type Evento } from '../services/ministries/ministries.service';
import { usePublicContent } from '../hooks/usePublicContent';

type EventoConMinisterio = Evento & { ministerioNombres: string };

export const EventosPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());

  const [eventosReales, setEventosReales] = useState<EventoConMinisterio[]>([]);
  const [loadingEventos, setLoadingEventos] = useState(true);
  const [errorEventos, setErrorEventos] = useState<string | null>(null);

  const { getText, getJson } = usePublicContent('EVENTOS');
  const headerLabel = getText('header', 'label', 'Agenda');
  const headerTitle = getText('header', 'titulo', 'Eventos y Actividades');
  const headerSubtitle = getText('header', 'subtitulo', 'Únete a nosotros en nuestras celebraciones y actividades especiales');
  const nextEventBadge = getText('proximo_evento', 'badge', 'Próximo Evento Especial');
  const countdownLabel = getText('proximo_evento', 'cuenta_regresiva_label', 'Cuenta regresiva');
  const countdownTitle = getText('proximo_evento', 'cuenta_regresiva_titulo', 'Faltan para {eventTitle}');
  const countdownFooter = getText('proximo_evento', 'cuenta_regresiva_footer', 'El tiempo avanza...');
  const defaultEventDescription = getText('proximo_evento', 'descripcion_default', 'No te pierdas este evento especial con nuestra familia de fe.');
  const ctaReserve = getText('proximo_evento', 'cta_reservar', 'Reservar mi lugar');
  const ctaMoreInfo = getText('proximo_evento', 'cta_mas_info', 'Más información');
  const loadingText = getText('eventos_mes', 'cargando', 'Cargando eventos...');
  const errorText = getText('eventos_mes', 'error', 'No se pudieron cargar los eventos');
  const emptyText = getText('eventos_mes', 'vacio', 'No hay eventos programados en este momento.');
  const emptyMonthText = getText('eventos_mes', 'vacio_mes', 'No hay eventos programados para este mes.');
  const monthlyLabel = getText('eventos_mes', 'label', 'Programación');
  const monthlyTitle = getText('eventos_mes', 'titulo', 'Eventos del Mes');
  const monthlyDescription = getText('eventos_mes', 'descripcion', 'Descubre las actividades especiales programadas');
  const subscribeButton = getText('eventos_mes', 'boton_inscribirme', 'Inscribirme');
  const weeklyLabel = getText('actividades_semanales', 'label', 'Siempre presentes');
  const weeklyTitle = getText('actividades_semanales', 'titulo', 'Actividades Semanales');
  const weeklyDescription = getText('actividades_semanales', 'descripcion', 'Nuestro cronograma semanal de servicios y reuniones');
  const eventosRecurrentes = getJson<{ titulo: string; dia: string; hora: string; tipo: string; icono: string }[]>('actividades_semanales', 'items', [
    { titulo: 'Culto Dominical', dia: 'Domingos', hora: '9:00 AM', tipo: 'culto', icono: '☀️' },
    { titulo: 'Servicio de Oración', dia: 'Miércoles', hora: '7:00 PM', tipo: 'oracion', icono: '🙏' },
    { titulo: 'Reunión de Jóvenes', dia: 'Viernes', hora: '7:00 PM', tipo: 'juventud', icono: '🎵' },
    { titulo: 'Escuela Dominical', dia: 'Domingos', hora: '8:00 AM', tipo: 'estudio', icono: '📖' },
    { titulo: 'Células Familiares', dia: 'Martes', hora: '7:30 PM', tipo: 'celula', icono: '🏠' },
    { titulo: 'Ministerio de Damas', dia: 'Jueves', hora: '2:00 PM', tipo: 'damas', icono: '👩' },
  ]);
  const calendarLabel = getText('calendario', 'label', 'Planifica');
  const calendarTitle = getText('calendario', 'titulo', 'Calendario Mensual');
  const calendarDescription = getText('calendario', 'descripcion', 'Mantente al día con todas nuestras actividades');
  const calendarButton = getText('calendario', 'boton', 'Ver Calendario Completo');
  const ctaLabel = getText('cta_final', 'label', 'Invitación');
  const ctaTitle = getText('cta_final', 'titulo', '¿Quieres ser parte de nuestros eventos?');
  const ctaDescription = getText('cta_final', 'descripcion', 'Únete a nuestra familia de fe y participa en todas nuestras actividades y celebraciones especiales.');
  const ctaJoin = getText('cta_final', 'boton_unirse', 'Únete a nosotros');
  const ctaLocation = getText('cta_final', 'boton_ubicacion', 'Ver Ubicación');

  useEffect(() => {
    let cancelled = false;
    const fetchEventos = async () => {
      setLoadingEventos(true);
      setErrorEventos(null);
      try {
        const eventos = await MinistriesService.findAllEventosPublic();
        const todosEventos: EventoConMinisterio[] = (eventos || []).map((ev) => ({
          ...ev,
          ministerioNombres: (ev.ministerios || []).map((m: any) => m.nombre).join(', ') || 'Iglesia',
        }));
        todosEventos.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
        if (!cancelled) {
          setEventosReales(todosEventos);
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Error al cargar eventos';
          setErrorEventos(msg);
        }
      } finally {
        if (!cancelled) {
          setLoadingEventos(false);
        }
      }
    };
    fetchEventos();
    return () => {
      cancelled = true;
    };
  }, []);

  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);

  const esEventoPasado = (fecha: string) => fecha.slice(0, 10) < todayStr;

  const proximoEvento = useMemo(() => {
    return eventosReales.find((ev) => !esEventoPasado(ev.fecha)) || eventosReales[0] || null;
  }, [eventosReales, todayStr]);

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'especial':
        return 'bg-red-700';
      case 'juventud':
        return 'bg-purple-800';
      case 'bautismo':
        return 'bg-blue-700';
      case 'culto':
        return 'bg-yellow-600';
      case 'oracion':
        return 'bg-indigo-700';
      case 'estudio':
        return 'bg-amber-700';
      case 'celula':
        return 'bg-purple-700';
      case 'damas':
        return 'bg-rose-700';
      default:
        return 'bg-stone-600';
    }
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  const formatearFecha = (fecha: string) => {
    const [year, month, day] = fecha.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return fecha;
    const d = new Date(year, month - 1, day);
    if (isNaN(d.getTime())) return fecha;
    return d.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatearHora = (horaInicio?: string, horaFin?: string) => {
    if (horaInicio && horaFin) return `${horaInicio} - ${horaFin}`;
    if (horaInicio) return horaInicio;
    if (horaFin) return horaFin;
    return 'Hora por definir';
  };

  // Función simple de countdown (sin componente externo)
  const CountdownDisplay = ({ targetDate, eventTitle }: { targetDate: string; eventTitle: string }) => {
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [progress, setProgress] = useState(0);

    useEffect(() => {
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const target = new Date(targetDate).getTime();
        const difference = target - now;

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);

          setTimeLeft({ days, hours, minutes, seconds });

          // Calcular progreso (asumiendo que empezamos a contar desde 30 días antes)
          const totalDuration = 30 * 24 * 60 * 60 * 1000; // 30 días en milisegundos
          const elapsed = totalDuration - difference;
          const progressPercentage = Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);
          setProgress(progressPercentage);
        } else {
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          setProgress(100);
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [targetDate]);

    const getUnitColor = (unit: string) => {
      switch (unit) {
        case 'days':
          return 'bg-stone-700';
        case 'hours':
          return 'bg-stone-600';
        case 'minutes':
          return 'bg-purple-800';
        case 'seconds':
          return 'bg-stone-500';
        default:
          return 'bg-stone-600';
      }
    };

    const TimeUnit = ({ value, label, color }: { value: number; label: string; color: string }) => (
      <div className="flex flex-col items-center">
        <div className={`relative w-16 h-16 lg:w-20 lg:h-20 ${color} rounded-xl flex items-center justify-center shadow-lg mb-2`}>
          <span className="text-stone-50 text-xl lg:text-2xl font-bold">
            {value.toString().padStart(2, '0')}
          </span>
        </div>
        <span className="text-sm lg:text-base font-medium text-stone-500 uppercase tracking-wide">
          {label}
        </span>
      </div>
    );

    return (
      <div className="text-center mb-6">
        <div className="mb-4">
          <p className="text-stone-500 text-sm uppercase tracking-[0.2em] mb-2 font-medium">{countdownLabel}</p>
          <h3 className="text-lg lg:text-xl font-serif font-bold text-stone-800 mb-2">
            {countdownTitle.replace('{eventTitle}', eventTitle)}
          </h3>
          <div className="w-12 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto rounded-full"></div>
        </div>
        
        <div className="flex justify-center items-center space-x-4 lg:space-x-6">
          <TimeUnit 
            value={timeLeft.days} 
            label="Días" 
            color={getUnitColor('days')}
          />
          
          <div className="text-2xl lg:text-3xl font-bold text-stone-300">:</div>
          
          <TimeUnit 
            value={timeLeft.hours} 
            label="Horas" 
            color={getUnitColor('hours')}
          />
          
          <div className="text-2xl lg:text-3xl font-bold text-stone-300">:</div>
          
          <TimeUnit 
            value={timeLeft.minutes} 
            label="Min" 
            color={getUnitColor('minutes')}
          />
          
          <div className="text-2xl lg:text-3xl font-bold text-stone-300">:</div>
          
          <TimeUnit 
            value={timeLeft.seconds} 
            label="Seg" 
            color={getUnitColor('seconds')}
          />
        </div>
        
        {/* Barra de progreso visual */}
        <div className="mt-4 max-w-md mx-auto">
          <div className="w-full bg-stone-200 rounded-full h-1.5">
            <div 
              className="bg-gradient-to-r from-purple-600 to-red-500 h-1.5 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-xs text-stone-400 mt-1.5">
            {countdownFooter}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen transition-colors duration-300 bg-stone-100">
      
      {/* Header */}
      <section className="relative overflow-hidden pt-32 pb-12 px-6">
        <div className="absolute inset-0 bg-stone-200/30"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium animate-fade-in">{headerLabel}</p>
          <h1 className="text-5xl md:text-6xl font-serif font-bold mb-4 animate-fade-in text-stone-800">
            {headerTitle}
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-6 rounded-full animate-slide-up delay-100"></div>
          <p className="text-lg text-stone-600 animate-slide-up delay-200">
            {headerSubtitle}
          </p>
        </div>
      </section>

      {/* Contador Regresivo Destacado */}
      {proximoEvento && (
        <section className="py-8 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl p-6 lg:p-8 bg-white backdrop-blur-sm border border-stone-200/60 shadow-lg">
              
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 px-5 py-1.5 bg-purple-100 rounded-full mb-3">
                    <span className="text-purple-700 text-sm font-semibold uppercase tracking-wide">
                      {nextEventBadge}
                    </span>
                  </div>
                  
                  <h2 className="text-3xl lg:text-4xl font-serif font-bold text-stone-800 mb-3">
                    {proximoEvento.titulo}
                  </h2>
                  
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 lg:gap-4 text-stone-600">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📅</span>
                      <span className="font-medium">{formatearFecha(proximoEvento.fecha)}</span>
                    </div>
                    <div className="hidden sm:block w-1.5 h-1.5 bg-stone-300 rounded-full"></div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🕐</span>
                      <span className="font-medium">{formatearHora(proximoEvento.horaInicio, proximoEvento.horaFin)}</span>
                    </div>
                    <div className="hidden sm:block w-1.5 h-1.5 bg-stone-300 rounded-full"></div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📍</span>
                      <span className="font-medium">{proximoEvento.lugar || 'Lugar por definir'}</span>
                    </div>
                  </div>
                </div>
                
                {/* Contador Regresivo */}
                <CountdownDisplay 
                  targetDate={`${proximoEvento.fecha.slice(0, 10)}T${proximoEvento.horaInicio ? proximoEvento.horaInicio.padEnd(8, ':00').slice(0, 8) : '00:00:00'}`}
                  eventTitle={proximoEvento.titulo}
                />
                
                <div className="text-center">
                  <p className="text-base lg:text-lg text-stone-600 mb-6 max-w-2xl mx-auto">
                    {proximoEvento.descripcion || defaultEventDescription}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button 
                      onClick={() => setIsFormOpen(true)}
                      className="px-6 py-3 rounded-xl font-semibold text-base transition-all duration-300 transform hover:scale-105 bg-purple-800 text-white hover:bg-purple-700 shadow-lg"
                    >
                      {ctaReserve}
                    </button>
                    <button className="px-6 py-3 rounded-xl font-semibold text-base transition-all duration-300 transform hover:scale-105 border border-stone-300 text-stone-700 hover:bg-stone-50">
                      {ctaMoreInfo}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Eventos del Mes */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium text-center">{monthlyLabel}</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-800 mb-2 text-center">
            {monthlyTitle}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-6 rounded-full"></div>
          <p className="text-center text-stone-500 mb-10">
            {monthlyDescription}
          </p>

          {/* Navegación de mes */}
          <div className="flex items-center justify-center gap-4 mb-10">
            <button
              onClick={() => setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              className="p-2 rounded-full transition-colors hover:bg-stone-100 text-stone-600 bg-white shadow-sm border border-stone-200/60"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-serif font-semibold text-stone-800 min-w-[180px] text-center">
              {monthNames[selectedMonth.getMonth()]} {selectedMonth.getFullYear()}
            </h3>
            <button
              onClick={() => setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              className="p-2 rounded-full transition-colors hover:bg-stone-100 text-stone-600 bg-white shadow-sm border border-stone-200/60"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {loadingEventos && (
            <div className="flex items-center justify-center py-12">
              <svg className="animate-spin h-8 w-8 text-purple-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="ml-3 text-stone-600">{loadingText}</span>
            </div>
          )}

          {!loadingEventos && errorEventos && (
            <div className="max-w-xl mx-auto bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-800 mb-8">
              <p className="font-medium">{errorText}</p>
              <p className="text-sm mt-1">{errorEventos}</p>
            </div>
          )}

          {!loadingEventos && !errorEventos && eventosReales.length === 0 && (
            <div className="text-center text-stone-600 py-12">
              {emptyText}
            </div>
          )}

          {!loadingEventos && !errorEventos && eventosReales.length > 0 && (
            (() => {
              const year = selectedMonth.getFullYear();
              const month = selectedMonth.getMonth();
              const eventosDelMes = eventosReales
                .filter((ev) => {
                  const [y, m] = ev.fecha.slice(0, 10).split('-').map(Number);
                  return y === year && m - 1 === month && !esEventoPasado(ev.fecha);
                })
                .sort((a, b) => a.fecha.localeCompare(b.fecha));

              if (eventosDelMes.length === 0) {
                return (
                  <div className="text-center text-stone-600 py-12 bg-white/60 rounded-2xl border border-stone-200/60">
                    {emptyMonthText}
                  </div>
                );
              }

              return (
                <div className="relative group">
                  <div className="overflow-x-auto pb-4 -mx-6 px-6 scrollbar-hide">
                    <div className="flex gap-6 snap-x snap-mandatory">
                      {eventosDelMes.map((evento) => (
                        <div
                          key={evento.id}
                          className="snap-start shrink-0 w-80 sm:w-96 flex flex-col rounded-2xl border overflow-hidden transition-all duration-300 bg-white border-stone-200/60 hover:shadow-lg hover:-translate-y-1"
                        >
                          {/* Cabecera con fecha destacada */}
                          <div className={`relative h-24 flex items-center justify-center ${getTipoColor('especial')}`}>
                            <div className="text-center text-white">
                              <p className="text-3xl font-bold">{evento.fecha.slice(8, 10)}</p>
                              <p className="text-sm font-medium uppercase tracking-wide opacity-90">
                                {monthNames[month].slice(0, 3)}
                              </p>
                            </div>
                          </div>

                          {/* Contenido */}
                          <div className="p-5 flex-1 flex flex-col">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h3 className="text-lg font-serif font-bold leading-tight text-stone-900">
                                {evento.titulo}
                              </h3>
                            </div>
                            <span className="inline-flex self-start text-xs font-semibold px-2 py-0.5 rounded-full mb-3 bg-purple-100 text-purple-700">
                              {evento.ministerioNombres}
                            </span>

                            <div className="space-y-1.5 text-sm text-stone-600">
                              <div className="flex items-center gap-2">
                                <span>🕐</span>
                                <span>{formatearHora(evento.horaInicio, evento.horaFin)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span>📍</span>
                                <span className="truncate">{evento.lugar || 'Lugar por definir'}</span>
                              </div>
                            </div>

                            {evento.descripcion && (
                              <p className="text-sm mt-3 line-clamp-2 text-stone-500">
                                {evento.descripcion}
                              </p>
                            )}

                            <div className="mt-auto pt-4">
                              <button
                                onClick={() => setIsFormOpen(true)}
                                className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 bg-purple-800 text-white hover:bg-purple-700"
                              >
                                {subscribeButton}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()
          )}
        </div>
      </section>

      {/* Eventos Semanales */}
      <section className="py-24 px-6 bg-stone-200/40">
        <div className="max-w-7xl mx-auto">
          <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium text-center">{weeklyLabel}</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-800 mb-2 text-center">
            {weeklyTitle}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-6 rounded-full"></div>
          <p className="text-center text-stone-500 mb-16">
            {weeklyDescription}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eventosRecurrentes.map((evento, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 shadow-sm border border-stone-200/60 hover:shadow-lg transition-all duration-300 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 ${getTipoColor(evento.tipo)} rounded-lg flex items-center justify-center shadow-sm`}>
                    <span className="text-white text-lg">{evento.icono}</span>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-serif font-bold text-stone-800 mb-1">
                      {evento.titulo}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-stone-600">
                      <span className="font-medium">{evento.dia}</span>
                      <span className="font-medium text-purple-700">{evento.hora}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendario de Eventos */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium">{calendarLabel}</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-800 mb-2">
            {calendarTitle}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-6 rounded-full"></div>
          <p className="text-stone-500 mb-16">
            {calendarDescription}
          </p>

          {/* Calendario Simple */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200/60">
            {/* Días de la semana */}
            <div className="grid grid-cols-7 gap-4 mb-8">
              {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((dia) => (
                <div key={dia} className="text-center font-bold text-stone-700 py-2">
                  {dia}
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <div className="w-24 h-24 bg-stone-200/60 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-stone-500 text-2xl">📅</span>
              </div>
              <p className="text-stone-600 mb-6 font-serif italic">
                [Aquí se integraría un calendario interactivo completo con todos los eventos]
              </p>
              <button 
                onClick={() => setIsCalendarOpen(true)}
                className="bg-purple-800 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 hover:bg-purple-700"
              >
                {calendarButton}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="rounded-2xl p-12 bg-stone-200/30 border border-stone-200/60">
            <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium">{ctaLabel}</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-2 text-stone-800">
              {ctaTitle}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-stone-600 mb-8">
              {ctaDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsFormOpen(true)}
                className="bg-purple-800 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                {ctaJoin}
              </button>
              <button className="border border-stone-400 text-stone-700 hover:bg-stone-800 hover:text-stone-50 hover:border-stone-800 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105">
                {ctaLocation}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de Inscripción */}
      <MembershipForm 
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        eventTitle={proximoEvento?.titulo || 'Próximo Evento'}
      />
      
      {/* Modal de Calendario */}
      <CalendarView 
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
      />
    </div>
  );
};

export default EventosPage;
